import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Message.module.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash,
  Plus,
  MessageSquare,
  Calendar,
  Type,
  FileText,
  ArrowRight,
  X
} from 'lucide-react';
import { getMessages, createMessage, updateMessage, deleteMessage, sendMessageEmails } from '../../Fetch/fetchMessage';

// MessageCard Component for Mobile View
const MessageCard = ({ message, onEdit, onDelete, onWhatsAppShare, onSendEmails, formatDate, loading }) => (
  <div className={styles.dataCard}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        <MessageSquare size={16} className={styles.cardIcon} />
        <span>{message.title}</span>
      </div>
      <div className={styles.cardHeaderActions}>
        <span className={styles.dateBadge}>
          {formatDate(message.createDate)}
        </span>
        <ActionMenu
          message={message}
          onEdit={onEdit}
          onDelete={onDelete}
          onWhatsAppShare={onWhatsAppShare}
          onSendEmails={onSendEmails}
          loading={loading}
        />
      </div>
    </div>
    <div className={styles.cardBody}>
      <div className={styles.cardInfo}>
        <FileText size={14} />
        <span className={styles.messageBody}>{message.body}</span>
      </div>
      <div className={styles.cardInfo}>
        <Calendar size={14} />
        <span>נוצר: {formatDate(message.createDate)}</span>
      </div>
    </div>
  </div>
);

// MessageForm Component
const MessageForm = ({ message, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: message?.title || '',
    body: message?.body || ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'כותרת היא שדה חובה';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'תוכן ההודעה הוא שדה חובה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;

      if (!buildingId) {
        alert('שגיאה: לא נמצא מזהה בניין');
        return;
      }

      let savedMessage;
      if (message) {
        // Update existing message
        savedMessage = await updateMessage(message.id, formData);
      } else {
        // Create new message
        savedMessage = await createMessage({
          ...formData,
          buildingId: buildingId
        });
      }

      onSave(savedMessage);
    } catch (error) {
      alert('שגיאה בשמירת ההודעה: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.messageFormContainer}>
      <div className={styles.formHeader}>
        <button
          className={styles.backButton}
          onClick={onCancel}
          type="button"
        >
          <ArrowRight size={20} />
        </button>
        <h2>{message ? 'עריכת הודעה' : 'הוספת הודעה חדשה'}</h2>
      </div>

      <form className={styles.messageForm} onSubmit={handleSubmit}>
        <div className={styles.formSection}>
          <h3>פרטי ההודעה</h3>

          <div className={styles.formGroup}>
            <label htmlFor="title">כותרת <span className={styles.required}>*</span></label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={errors.title ? styles.error : ''}
              placeholder="הכנס כותרת להודעה"
            />
            {errors.title && <span className={styles.fieldError}>{errors.title}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="body">תוכן ההודעה <span className={styles.required}>*</span></label>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleChange}
              className={errors.body ? styles.error : ''}
              placeholder="הכנס את תוכן ההודעה"
              rows={6}
            />
            {errors.body && <span className={styles.fieldError}>{errors.body}</span>}
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.saveButton}
            disabled={loading}
          >
            {loading ? 'שומר...' : (message ? 'עדכן הודעה' : 'הוסף הודעה')}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            ביטול
          </button>

        </div>
      </form>
    </div>
  );
};

// Action Menu Modal Component (replaces dropdown)
const ActionMenu = ({ message, onEdit, onDelete, onWhatsAppShare, onSendEmails, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    {
      label: '📱 שתף WhatsApp',
      onClick: () => onWhatsAppShare(message),
      disabled: loading
    },
    {
      label: '📧 שלח במייל',
      onClick: () => onSendEmails(message),
      disabled: loading
    },
    {
      label: '✏️ ערוך',
      onClick: () => onEdit(message),
      disabled: loading
    },
    {
      label: '🗑️ מחק',
      onClick: () => onDelete(message.id),
      disabled: loading,
      isDanger: true
    }
  ];

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const Messages = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buildingId, setBuildingId] = useState('');

  // Statistics
  const totalMessages = messages.length;
  const thisMonthMessages = messages.filter(m => {
    const messageDate = new Date(m.createDate);
    const now = new Date();
    return messageDate.getMonth() === now.getMonth() &&
      messageDate.getFullYear() === now.getFullYear();
  }).length;

  // Load buildingId on component mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (user?.buildingId) {
        setBuildingId(user.buildingId);
      } else {
      }
    } catch (error) {
      localStorage.removeItem('user');
    }
  }, []);

  // Load messages data
  useEffect(() => {
    if (buildingId) {
      loadMessages();
    }
  }, [buildingId]);

  // Add event listener for building changes
  useEffect(() => {
    const handleBuildingChange = () => {
      const userData = JSON.parse(localStorage.getItem('user'));
      const newBuildingId = userData?.buildingId;

      if (newBuildingId && newBuildingId !== buildingId) {
        setBuildingId(newBuildingId);
      }
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('messageDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('messageDataChanged', handleBuildingChange);
    };
  }, [buildingId]);

  const loadMessages = async () => {
    try {
      setLoading(true);

      if (!buildingId) {
        setMessages([]);
        return;
      }

      const data = await getMessages(buildingId);
      setMessages(data);

      // Reset filters and pagination when loading new building data
      setSearchTerm('');
      setCurrentPage(1);

    } catch (error) {
      setMessages([]);
      alert('שגיאה בטעינת ההודעות: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter messages based on search term
  useEffect(() => {
    let filtered = messages;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(message => {
        const searchLower = searchTerm.toLowerCase();
        return (
          message.title?.toLowerCase().includes(searchLower) ||
          message.body?.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredMessages(filtered);
    setCurrentPage(1);
  }, [searchTerm, messages]);

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
  };

  // Pagination
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAddMessage = () => {
    setEditingMessage(null);
    setShowForm(true);
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setShowForm(true);
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) {
      try {
        setLoading(true);
        await deleteMessage(id);
        const updatedMessages = messages.filter(message => message.id !== id);
        setMessages(updatedMessages);
      } catch (error) {
        alert('שגיאה במחיקת ההודעה: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleWhatsAppShare = (message) => {
    // Create WhatsApp message with message content
    const whatsappMessage = `📢 הודעה מועד הבית: ${message.title}

📝 ${message.body}

תאריך: ${formatDate(message.createDate)}

מוזמנים לקרוא ולהגיב!`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);

    // Create WhatsApp Web URL
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;

    // Open in new tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSendEmails = async (message) => {
    if (window.confirm(`האם אתה בטוח שברצונך לשלח את ההודעה "${message.title}" לכל הדיירים בבניין?`)) {
      setLoading(true);
      try {
        const result = await sendMessageEmails(buildingId, message.id);

        if (result.success) {
          alert(`ההודעה נשלחה בהצלחה! ${result.sentCount} מתוך ${result.totalCount} מיילים נשלחו.`);
        } else {
          alert(`שגיאה בשליחת ההודעה: ${result.message}`);
        }
      } catch (error) {
        alert(`שגיאה בשליחת ההודעה: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMessageSaved = (savedMessage) => {
    if (editingMessage) {
      // Update existing message
      setMessages(prev => prev.map(m =>
        m.id === savedMessage.id ? savedMessage : m
      ));
    } else {
      // Add new message
      setMessages(prev => [savedMessage, ...prev]);
    }
    setShowForm(false);
    setEditingMessage(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Show MessageForm if form is active
  if (showForm) {
    return (
      <MessageForm
        message={editingMessage}
        onSave={handleMessageSaved}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <div className={styles.messagesManagement}>
      <div className={styles.header}>
        <h1>ניהול הודעות</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <MessageSquare size={24} className={styles.statIcon} />
            <div>
              <h3>סה"כ הודעות</h3>
              <p className={styles.statValue}>{totalMessages}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <Calendar size={24} className={styles.statIcon} />
            <div>
              <h3>הודעות החודש</h3>
              <p className={styles.statValue}>{thisMonthMessages}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          {/* Search Button - Opens modal for both desktop and mobile */}
          <button
            className={styles.searchButton}
            onClick={() => setShowMobileSearch(true)}
            title="חיפוש וסינון"
          >
            <Search size={20} />
            <span>חיפוש וסינון</span>
            {searchTerm && (
              <span className={styles.activeFiltersIndicator}>1</span>
            )}
          </button>
        </div>

        <button className={styles.addButton} onClick={handleAddMessage}>
          <Plus size={20} />
          הוסף הודעה חדשה
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען הודעות...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.messagesTable}>
              <thead>
                <tr>
                  <th>{t('title')}</th>
                  <th>{t('messageContent')}</th>
                  <th>{t('creationDate')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {currentMessages.length > 0 ? (
                  currentMessages.map((message) => (
                    <tr key={message.id}>
                      <td className={styles.messageTitle}>
                        {message.title}
                      </td>
                      <td className={styles.messageBodyCell}>
                        {truncateText(message.body)}
                      </td>
                      <td>{formatDate(message.createDate)}</td>
                      <td className={styles.actionsCell}>
                        <ActionMenu
                          message={message}
                          onEdit={handleEditMessage}
                          onDelete={handleDeleteMessage}
                          onWhatsAppShare={handleWhatsAppShare}
                          onSendEmails={handleSendEmails}
                          loading={loading}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className={styles.noResults}>
                      {searchTerm ? 'לא נמצאו הודעות התואמות לחיפוש' : 'אין הודעות'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className={styles.mobileCardsContainer}>
            <div className={styles.cardsGrid}>
              {currentMessages.length > 0 ? (
                currentMessages.map((message) => (
                  <MessageCard
                    key={message.id}
                    message={message}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                    onWhatsAppShare={handleWhatsAppShare}
                    onSendEmails={handleSendEmails}
                    formatDate={formatDate}
                    loading={loading}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  {searchTerm ? 'לא נמצאו הודעות התואמות לחיפוש' : 'אין הודעות'}
                </div>
              )}
            </div>
          </div>

          {filteredMessages.length > 0 && (
            <div className={styles.pagination}>
              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  <ChevronRight size={20} />
                </button>
                <span className={styles.pageInfo}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className={styles.resultsPerPage}>
                <span>תוצאות בעמוד:</span>
                <select
                  value={messagesPerPage}
                  onChange={(e) => {
                    setMessagesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className={styles.totalInfo}>
                מציג {indexOfFirstMessage + 1}-{Math.min(indexOfLastMessage, filteredMessages.length)} מתוך {filteredMessages.length} הודעות
              </div>
            </div>
          )}
        </>
      )}

      {/* Mobile Search Button - Rendered outside main container */}
      {!showMobileSearch && createPortal(
        <button
          className={styles.mobileSearchButton}
          onClick={() => setShowMobileSearch(true)}
          aria-label="פתח חיפוש"
        >
          <Search size={22} />
          {searchTerm && (
            <span className={styles.searchIndicatorDot}></span>
          )}
        </button>,
        document.body
      )}

      {/* Mobile Search Modal */}
      {showMobileSearch && createPortal(
        <div className={styles.mobileSearchModal}>
          <div className={styles.mobileSearchHeader}>
            <h3>חיפוש וסינון</h3>
            <button className={styles.mobileSearchClose} onClick={() => setShowMobileSearch(false)}>
              <X size={24} />
            </button>
          </div>
          <div className={styles.mobileSearchContent}>
            <div className={styles.mobileSearchField}>
              <Search size={20} className={styles.mobileSearchIcon} />
              <input
                type="text"
                placeholder="חיפוש הודעות..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {searchTerm && (
              <button
                className={styles.mobileSearchClear}
                onClick={clearFilters}
              >
                נקה הכל
              </button>
            )}
            <button className={styles.mobileSearchApply} onClick={() => setShowMobileSearch(false)}>
              החל
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Messages;